import {
  useWSModalCustom,
  WSAlert,
  WSButton,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSectionToolbar,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Payer,
  PayerEngagement,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import { IPayerEngagementResponse } from "@wingspanhq/payments/dist/interfaces";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import {
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers,
  queriesWorkLoggingConfigs,
  queriesWorkLogs
} from "@query";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import { selectorPayerDisplayName } from "../../../shared/selectors/selectorPayerDisplayName";
import { INVOICING_PATH } from "../../Invoicing";
import { PAYERS_PATH } from "../../Payers/paths";
import { SelectPayerEngagement } from "../../Payers/components/SelectPayerEngagement";
import { WorkLogEngagementDetails } from "../components/WorkLogEngagementDetails";
import {
  routeHelpersWorkLogPayeeDetails,
  routeHelpersWorkLogsPayeeInProgress
} from "../paths";

type Props = {
  defaultPayerId?: string;
  defaultPayerPayeeEngagementId?: string;
} & WSModalCustomProps<void>;

export const ModalCreateWorkLog: React.FC<Props> = ({
  defaultPayerId,
  defaultPayerPayeeEngagementId,
  ...props
}) => {
  const history = useHistory();
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const isPreselected = !!defaultPayerId && !!defaultPayerPayeeEngagementId;

  const { payerId, payerPayeeEngagementId } = useMemo(() => {
    if (!selectValue) {
      // Use preselected values while loading (before selectValue is set)
      if (isPreselected) {
        return {
          payerId: defaultPayerId,
          payerPayeeEngagementId: defaultPayerPayeeEngagementId
        };
      }
      return {};
    }
    const [payerId, engagementId] = selectValue.value.split("/");
    return { payerId, payerPayeeEngagementId: engagementId };
  }, [
    isPreselected,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  const queryEngagement = queriesPayerEngagements.getPayerEngagement.useQuery(
    { payerId: payerId!, payerEngagementId: payerPayeeEngagementId! },
    { enabled: !!payerId && !!payerPayeeEngagementId }
  );

  // Initialize selectValue when preselected values are provided
  useEffect(() => {
    if (
      isPreselected &&
      queryPayer.data &&
      queryEngagement.data &&
      !selectValue
    ) {
      const payerName = selectorPayerDisplayName(queryPayer.data);
      const engagementName = queryEngagement.data.engagementName;

      setSelectValue({
        value: `${defaultPayerId}/${defaultPayerPayeeEngagementId}`,
        label: `${payerName} • ${engagementName}`,
        // TODO (v3-migration): payer.email is empty for active payers, no wsName email source
        labels: [queryPayer.data.email].filter(Boolean) as string[],
        avatar: {
          type: "text",
          text: payerName
        }
      });
    }
  }, [
    isPreselected,
    queryPayer.data,
    queryEngagement.data,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  // TODO(v3): dropped the v1 `filter[endDate][exists]=false` filter — v3
  // listWorkLogs has no exists-filter and periodStart/periodEnd are readonly, so
  // this may include draft work logs that already have a period end.
  const queryExistingWorkLogs = queriesWorkLogs.listWorkLogs.usePaginatedQuery(
    {
      filter: {
        payeeEngagementId: { eq: payerPayeeEngagementId },
        status: { anyOf: [WorkLogStatus.DRAFT] }
      }
    },
    { enabled: !!payerPayeeEngagementId }
  );

  const existingWorkLogs = queryExistingWorkLogs.flatData || [];

  const queryWorkLogConfig =
    queriesWorkLoggingConfigs.getWorkLoggingConfigForPayee.useQuery(
      { payerAccountId: payerId! },
      { enabled: !!payerId }
    );

  const isPayeeSubmissionDisabled =
    !!payerId &&
    !queryWorkLogConfig.isLoading &&
    queryWorkLogConfig.data?.allowPayeeCreatedWorkLogs?.enabled === false;

  // Check if any selectable assignments exist (with work logging enabled)
  const querySelectablePayerRows =
    queriesPayerRows.searchPayerRows.usePaginatedQuery({
      filter: { engagements_isWorkLoggingEnabled: { eq: true } }
    });

  const hasNoSelectableAssignments =
    !querySelectablePayerRows.isLoading &&
    (querySelectablePayerRows.flatData?.length ?? 0) === 0;

  const [createWorkLog, createWorkLogMeta] =
    queriesWorkLogs.createWorkLog.useMutation();

  const handleAddNewPayer = useCallback(
    (payer: Payer, engagements: PayerEngagement[]) => {
      if (engagements.length > 0) {
        const payerEngagement = engagements[0];
        const payerName = selectorPayerDisplayName(payer);

        setSelectValue({
          value: `${payer.id}/${payerEngagement.id}`,
          label: `${payerName} • ${payerEngagement.engagementName}`,
          labels: payer.email ? [payer.email] : [],
          avatar: {
            type: "text",
            text: payerName
          }
        });
      }
    },
    []
  );

  const handleViewExistingWorkLogs = useCallback(() => {
    history.push(
      routeHelpersWorkLogsPayeeInProgress.createLocationObject({
        search: { filter: { assignmentId: payerPayeeEngagementId } }
      })
    );
    props.onClose();
  }, [history, payerPayeeEngagementId, props]);

  return (
    <WSModal
      title="New work log"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Tertiary",
            label: "Cancel",
            onClick: () => props.onClose()
          },
          {
            kind: "Primary",
            label: "Create new work log",
            onClick: () => {
              if (!payerPayeeEngagementId) return;

              createWorkLog(
                {
                  // TODO(v3): WorkLogCreate has no startDate/endDate/userId — the
                  // period is server-derived, so the v1 `startDate: new Date()` is dropped.
                  workLogCreate: {
                    payeeEngagementId: payerPayeeEngagementId
                  }
                },
                {
                  onSuccess: workLog => {
                    history.push(
                      routeHelpersWorkLogPayeeDetails.createLocationObject({
                        workLogId: workLog.id!
                      })
                    );
                    props.onClose();
                  }
                }
              );
            },
            loading: createWorkLogMeta.isLoading,
            disabled:
              hasNoSelectableAssignments ||
              !payerPayeeEngagementId ||
              queryPayer.isLoading ||
              queryEngagement.isLoading ||
              queryExistingWorkLogs.isLoading ||
              queryWorkLogConfig.isLoading ||
              isPayeeSubmissionDisabled
          }
        ]
      }}
      divider
      {...props}
    >
      <WSList gap="L">
        <WSElement>
          <WSSectionToolbar title="Client assignment *" mb="XS" />
          {hasNoSelectableAssignments ? (
            <WSEmptyState
              type="search"
              orientation="horizontal"
              hasBorder
              title="No assignments available"
              description="Only assignments you can create work logs for will appear here"
            />
          ) : (
            <>
              <WSText.ParagraphSm color="gray500" mb="M">
                Select the client assignment to log work for
              </WSText.ParagraphSm>
              <SelectPayerEngagement
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={!isPreselected}
                value={selectValue}
                onChange={setSelectValue}
                onAddNewPayer={handleAddNewPayer}
                baseRequest={{
                  filter: {
                    engagements_isWorkLoggingEnabled: { eq: true }
                  }
                }}
                menuFooterText="Don't see an assignment? Some assignments are managed by clients directly."
                customMenuFooterAction={{
                  label: "View clients",
                  rightIcon: "chevron-right",
                  onClick: () => {
                    history.push(PAYERS_PATH);
                    props.onClose();
                  }
                }}
              />
            </>
          )}
        </WSElement>

        <WSElement colorBackground="gray50" px="M" py="S">
          <WSText.ParagraphXs weight="medium" color="gray600" mb="XS">
            Don't see an assignment?
          </WSText.ParagraphXs>
          <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
            <WSText.ParagraphXs color="gray400">
              Some assignments use direct invoicing
            </WSText.ParagraphXs>
            <WSButton
              kind="Link"
              size="S"
              rightIcon="chevron-right"
              onClick={() => {
                history.push(INVOICING_PATH + "/create");
                props.onClose();
              }}
            >
              Create invoice
            </WSButton>
          </WSFlexBox.CenterY>
          <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
            <WSText.ParagraphXs color="gray400">
              Some assignments are managed by clients directly.
            </WSText.ParagraphXs>
            <WSButton
              kind="Link"
              size="S"
              rightIcon="chevron-right"
              onClick={() => {
                history.push(PAYERS_PATH);
                props.onClose();
              }}
            >
              View clients
            </WSButton>
          </WSFlexBox.CenterY>
        </WSElement>

        {!hasNoSelectableAssignments && payerPayeeEngagementId && (
          <WorkLogEngagementDetails
            shimmer={queryPayer.isLoading || queryEngagement.isLoading}
            payer={
              queryPayer.data
                ? selectorPayerResponseFromV3(queryPayer.data)
                : undefined
            }
            // WorkLogEngagementDetails only reads `.engagementName` (unlike
            // CardWorkItems' `resources.payerPayeeEngagement`, which needs the
            // full v1 shape for its dynamic enum lookups) — a minimal stub is
            // safe here; don't reuse this pattern where the full object is read.
            payerPayeeEngagement={
              queryEngagement.data
                ? ({
                    engagementName: queryEngagement.data.engagementName
                  } as IPayerEngagementResponse)
                : undefined
            }
          />
        )}

        {!hasNoSelectableAssignments && isPayeeSubmissionDisabled && (
          <WSAlert
            theme="info"
            icon="info-circle"
            title="Client does not allow work log submissions"
          >
            Only the client can create work logs on your behalf for this
            assignment.
          </WSAlert>
        )}

        {!hasNoSelectableAssignments && existingWorkLogs.length > 0 && (
          <WSAlert
            theme="info"
            icon="info-circle"
            title="Existing work logs found"
            action={{
              text: "View existing work logs",
              onClick: handleViewExistingWorkLogs
            }}
          >
            You have {existingWorkLogs.length} in-progress work log
            {existingWorkLogs.length > 1 ? "s" : ""} for this assignment. Would
            you like to continue working on those, or start a fresh work log?
          </WSAlert>
        )}

        <WSErrorMessage contextKey="WorkLog" error={createWorkLogMeta.error} />
      </WSList>
    </WSModal>
  );
};

export const useModalCreateWorkLog = () => useWSModalCustom(ModalCreateWorkLog);
